import classnames from 'classnames';
import React, {Component} from 'react';
import ReactDOMCompat from '../../wui-core/src/reactCompat';
import {Warning, prefix} from "../../wui-core/src";
import createChainedFunction from '../../wui-core/src/utils/createChainedFunction';
import {WebUI} from "../../wui-core/src/index";
import {addEventListener} from '../../wui-core/src/eventListener';
import {AddEventLReturn} from '../../wui-overlay/src/utils/IUtils';
import ownerDocument from '../../wui-overlay/src/utils/ownerDocument';
import Notice from './Notice';
import {NewInstanceCb, NoticeProps, NotificationProps} from './iNotification';
import { OrNull } from '../../wui-core/src/types/iCore';

const {isShouldUpdate} = Warning;
let seed = 0;
const now = Date.now();
let maxCount: number | undefined;

function getUuid() {
    return `uNotification_${now}_${seed++}`;
}

const defaultProps = {
    animation: 'fade',
    keyboard: true,
    position: 'topRight',
    clsPrefix: "",
    transitionName: "",
    transition: true,
    destroyWithKey: false,
    onEscapeKeyUp: () => {
    }
}

class BaseNotification extends Component<NotificationProps, {notices: NoticeProps[]}> {
    public _onDocumentKeyupListener: OrNull<AddEventLReturn> = null;

    state = {
        notices: [] as NoticeProps[]
    };

    componentDidMount() {
        const doc = ownerDocument(this);
        this._onDocumentKeyupListener = addEventListener(doc, 'keyup', this.handleDocumentKeyUp);
    }

    componentWillUnmount() {
        this._onDocumentKeyupListener?.remove();
        this._onDocumentKeyupListener = null;
    }

    handleDocumentKeyUp = (e: React.KeyboardEvent) => {
	    if (this.props.keyboard && e.keyCode === 27 && this.state.notices.length) {
	        this.setState(previousState => ({
	            notices: previousState.notices.slice(1),
	        }));
	        this.props.onEscapeKeyUp?.(e);
	    }
    }

    add = (notice: NoticeProps) => {
        const maxcount = this.props.maxCount || maxCount;
        const key = notice.key = notice.key || getUuid();
        this.setState(previousState => {
            const notices = previousState.notices;
            if (maxcount && maxcount === notices.length) {
                return { notices: [...notices.slice(1), notice] };
            }
            if (!notices.some(v => v.key === key)) {
                return { notices: [...notices, notice] };
            }
            return null;
        });
    }

    remove = (key: string | number) => {
        this.setState(previousState => ({
            notices: previousState.notices.filter(notice => notice.key !== key),
        }));
    }

    isKeyInNotices = (key: string | number) => {
        return this.state.notices.some(notice => notice.key !== null && notice.key !== undefined && notice.key === key)
    }

    handleClose = (key: string) => {
        this.setState(previousState => ({
            notices: previousState.notices.filter(notice => notice.key !== key)
        }));
    }

    clear = () => {
        this.setState({ notices: [] });
    }

    animateEnd = (_key: string, show: boolean, node: HTMLElement) => {
        if (node) {
            node.style.visibility = show ? 'visible' : 'hidden';
        }
    }

}

@WebUI({name: "notification", defaultProps})
class Notification extends BaseNotification {

    static defaultProps = defaultProps;
    static clear: () => void;
    static handleClose: (key: string) => void;
    static add: (notice: NoticeProps) => void;
    static newInstance: (notice: NotificationProps, callback: NewInstanceCb) => void;
    static success: (notice: NoticeProps) => void;
    static info: (notice: NoticeProps) => void;
    static warning: (notice: NoticeProps) => void;
    static warn: (notice: NoticeProps) => void;
    static error: (notice: NoticeProps) => void;
    static open: (notice: NoticeProps) => void;
    static close: (key: string) => void;
    static destroy: () => void;

    constructor(props: NotificationProps) {
        super(props);
        this.remove = this.remove.bind(this);
        Notification.clear = this.clear.bind(this);
        Notification.add = this.add.bind(this);
        Notification.handleClose = this.handleClose.bind(this);
    }

    render() {
	    const {
	        clsPrefix,
	        className,
	        position,
	        placement,
	        fieldid,
	        style,
	    } = this.props;
	    isShouldUpdate("Notification", this.props);
	    const noticeNodes = this.state.notices.map((notice) => {
	        const onClose = createChainedFunction(this.remove.bind(this, notice.key), notice.onClose);
	        const text = notice.content ? notice.content : notice.description;
	        const {key, ...noticeProps} = notice;
	        return (<Notice
	            key={key}
	            clsPrefix={clsPrefix}
	            {...noticeProps}
	            onClose={onClose}
	        >
	            {text}
	        </Notice>);
	    });
	    const classes = {
	        [clsPrefix as string]: 1,
	        [className ?? ""]: !!className,
	    };
	    if (placement || position) {
	        classes[`${clsPrefix}-${placement || position}`] = !!(placement || position);
	    }

	    return (
	        <div className={classnames(classes)} fieldid={fieldid || this.state.notices?.[0]?.fieldid} style={style}>
                {noticeNodes}
	        </div>
	    );
    }
}

Notification.newInstance = function newNotificationInstance(props = {}, callback) {
    maxCount = props.maxCount
    if (typeof callback !== 'function') {
        // eslint-disable-next-line no-console
        console.error('You must introduce callback as the second parameter of Notification.newInstance().')
        return
    }
    const {getPopupContainer, container: propsContainer, getContainer, ...others} = props
    let container = getPopupContainer || propsContainer || getContainer || document.body;
    if (typeof container == 'function') {
        container = (container as (node?: HTMLElement | undefined) => HTMLElement)()
    }

    const div = document.createElement('div');
    container.appendChild(div);
    const root = ReactDOMCompat.createRoot(div);

    let called = false;

    function ref(notification: Notification) {
        if (called || !notification) {
            return;
        }
        called = true;
        callback({
            notice(noticeProps) {
                notification.add(noticeProps);
            },
            removeNotice(key) {
                notification.remove(key);
            },
            component: notification,
            destroy() {
                const notices = notification.state.notices;
                if (notices.every(notice => !notice.destroyWithKey)) {
                    root.unmount();
                    try {
                        (container as HTMLElement).removeChild(div); // QDJCJS-25253 修复message挂载在页签内，刷新后实例仍存在，导致不再显示新消息的问题
                    } catch (e) {
                        // Do nothing
                    }
                    return false;
                }
                notices.forEach((notice, index) => {
                    if (!notice.destroyWithKey) {
                        const messageDom = div.querySelectorAll(`.${prefix}-message-notice`)[index]
                        root.unmount();
                        div.children[0].removeChild(messageDom);
                    }

                });
                return true
            },
            isKeyInNotices(key) {
                return notification.isKeyInNotices(key)
            }
        });
    }
    const renderDom = <Notification {...others} ref={ref}/>;
    ReactDOMCompat.flushSync(() => {
        root.render(renderDom);
    })
    // root.render(renderDom);
    // ReactDOM.render(renderDom, div);

};
Notification.success = function(properties) {
    Notification.add(Object.assign(properties, {color: 'success'}))
}
Notification.info = function(properties) {
    Notification.add(Object.assign(properties, {color: 'info'}))
}
Notification.warning = function(properties) {
    Notification.add(Object.assign(properties, {color: 'warning'}))
}
Notification.warn = function(properties) {
    Notification.add(Object.assign(properties, {color: 'warning'}))
}
Notification.error = function(properties) {
    Notification.add(Object.assign(properties, {color: 'danger'}))
}
Notification.open = function(properties) {
    Notification.add(properties)
}
Notification.close = function(properties) {
    Notification.handleClose(properties)
}
Notification.destroy = function() {
    Notification.clear()
}

export default Notification;
